// См. docs/data-layer.md §3.

import {
  QueryClient,
  useQuery as useReactQuery,
  useQueryClient,
  type Updater,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query'

import { HttpError } from '../http/HttpError'
import { httpRequest } from '../http/httpClient'
import type { HttpMethod } from '../http/types'
import { queryClient as singletonQueryClient } from '../queryClient'
import { CacheBehavior, getCacheSettings } from './cache'
import { buildQueryKey } from './keys'
import {
  createShouldRunSideEffect,
  runErrorSideEffects,
  runSuccessSideEffects,
} from './sideEffects'
import type { CommonSideEffects, DisabledSideEffectsConfig, QueryContext } from './types'

export type CreateQueryConfig<
  TInput,
  TApiOutput,
  TResult,
  TContext extends object = Record<string, never>,
> = {
  /** Стабильный строковый ключ, формат `entity.action`. Первый элемент queryKey */
  key: string
  /** Путь с `:param` либо функция от input */
  path: string | ((input: TInput) => string)
  /** По умолчанию GET */
  method?: HttpMethod
  /** Маппинг сырого ответа бэка в клиентский тип. Обязателен всегда */
  transform: (apiOutput: TApiOutput, input: TInput) => TResult
  /** По умолчанию для GET — весь input минус path-параметры */
  getSearchParams?: (input: TInput) => Record<string, unknown>
  /** Переопределение ключа. По умолчанию `[key, normalizedInput]` */
  getQueryKey?: (input: TInput) => unknown[]
  cacheBehavior?: CacheBehavior
  queryOptions?: Omit<UseQueryOptions<TResult, HttpError, TResult>, 'queryKey' | 'queryFn'>
  sideEffects?: CommonSideEffects<TResult, TInput, TContext>
  /** Хук, добавляющий контекст в side effects (нотификатор, роутер и т.п.) */
  useAdditionalContext?: () => TContext
  /** Брать данные из реестра моков вместо сети */
  mocked?: boolean
}

export type RuntimeQueryOptions<
  TResult,
  TInput,
  TContext extends object = Record<string, never>,
> = Omit<UseQueryOptions<TResult, HttpError, TResult>, 'queryKey' | 'queryFn'> &
  CommonSideEffects<TResult, TInput, TContext> & {
    disabledSideEffects?: DisabledSideEffectsConfig
  }

// void-вход → useQuery()/query() без аргументов; иначе аргумент обязателен
type QueryArgs<TInput, TResult, TContext extends object> = [TInput] extends [void]
  ? [input?: TInput, options?: RuntimeQueryOptions<TResult, TInput, TContext>]
  : [input: TInput, options?: RuntimeQueryOptions<TResult, TInput, TContext>]

type ImperativeQueryArgs<TInput> = [TInput] extends [void] ? [input?: TInput] : [input: TInput]

export type CacheController<TInput, TResult> = {
  invalidate: (options?: { input?: Partial<TInput> }) => Promise<void>
  reset: (options?: { input?: Partial<TInput> }) => Promise<void>
  remove: (options?: { input?: Partial<TInput> }) => void
  setData: (input: TInput, updater: Updater<TResult | undefined, TResult | undefined>) => void
  getData: (input: TInput) => TResult | undefined
  prefetch: (input: TInput) => Promise<void>
}

export type QueryFactory<TInput, TResult, TContext extends object = Record<string, never>> = {
  useQuery: (...args: QueryArgs<TInput, TResult, TContext>) => UseQueryResult<TResult, HttpError>
  useCacheController: () => CacheController<TInput, TResult>
  query: (...args: ImperativeQueryArgs<TInput>) => Promise<TResult>
  getQueryKey: (input?: Partial<TInput>) => unknown[]
  invalidate: (queryClient: QueryClient, options?: { input?: Partial<TInput> }) => Promise<void>
}

export function createQuery<
  TInput = void,
  TApiOutput = unknown,
  TResult = TApiOutput,
  TContext extends object = Record<string, never>,
>(
  config: CreateQueryConfig<TInput, TApiOutput, TResult, TContext>,
): QueryFactory<TInput, TResult, TContext> {
  const method = config.method ?? 'GET'
  const cacheSettings = getCacheSettings(config.cacheBehavior)

  const computeKey = (input?: Partial<TInput>): unknown[] => {
    if (config.getQueryKey && input !== undefined) {
      return config.getQueryKey(input as TInput)
    }
    return buildQueryKey(config.key, input)
  }

  const fetchAndTransform = async (input: TInput): Promise<TResult> => {
    const apiOutput = await httpRequest<TApiOutput, TInput>({
      path: config.path,
      method,
      input,
      getSearchParams: config.getSearchParams,
      mocked: config.mocked,
    })

    return config.transform(apiOutput, input)
  }

  const invalidateImpl = (
    client: QueryClient,
    options: { input?: Partial<TInput> } = {},
  ): Promise<void> =>
    client.invalidateQueries({ queryKey: computeKey(options.input), exact: false })

  const resetImpl = (
    client: QueryClient,
    options: { input?: Partial<TInput> } = {},
  ): Promise<void> => client.resetQueries({ queryKey: computeKey(options.input), exact: false })

  const removeImpl = (client: QueryClient, options: { input?: Partial<TInput> } = {}): void => {
    client.removeQueries({ queryKey: computeKey(options.input), exact: false })
  }

  const useQueryHook = (
    ...args: QueryArgs<TInput, TResult, TContext>
  ): UseQueryResult<TResult, HttpError> => {
    const [input, runtimeOptions] = args
    const reactQueryClient = useQueryClient()
    const additionalContext = (config.useAdditionalContext?.() ?? {}) as TContext

    const {
      disabledSideEffects,
      onErrorWithCode,
      onHttpError,
      onAnyError,
      onSuccess,
      ...restOptions
    } = (runtimeOptions ?? {}) as RuntimeQueryOptions<TResult, TInput, TContext>

    const runtimeSideEffects: CommonSideEffects<TResult, TInput, TContext> = {
      onErrorWithCode,
      onHttpError,
      onAnyError,
      onSuccess,
    }
    const shouldRun = createShouldRunSideEffect(disabledSideEffects)

    return useReactQuery<TResult, HttpError, TResult>({
      queryKey: computeKey(input as TInput),
      queryFn: async () => {
        const ctx = Object.assign(
          { input: input as TInput, queryClient: reactQueryClient },
          additionalContext,
        ) as QueryContext<TInput, TContext>

        try {
          const result = await fetchAndTransform(input as TInput)
          runSuccessSideEffects(result, ctx, config.sideEffects, runtimeSideEffects, shouldRun)
          return result
        } catch (error) {
          runErrorSideEffects(error, ctx, config.sideEffects, runtimeSideEffects, shouldRun)
          throw error
        }
      },
      ...cacheSettings,
      ...config.queryOptions,
      ...restOptions,
    })
  }

  const useCacheController = (): CacheController<TInput, TResult> => {
    const reactQueryClient = useQueryClient()

    return {
      invalidate: (options) => invalidateImpl(reactQueryClient, options),
      reset: (options) => resetImpl(reactQueryClient, options),
      remove: (options) => removeImpl(reactQueryClient, options),
      setData: (input, updater) => {
        reactQueryClient.setQueryData<TResult>(computeKey(input), updater)
      },
      getData: (input) => reactQueryClient.getQueryData<TResult>(computeKey(input)),
      prefetch: (input) =>
        reactQueryClient.prefetchQuery({
          queryKey: computeKey(input),
          queryFn: () => fetchAndTransform(input),
          ...cacheSettings,
        }),
    }
  }

  const query = (async (input?: TInput): Promise<TResult> => {
    return singletonQueryClient.fetchQuery({
      queryKey: computeKey(input as TInput),
      queryFn: () => fetchAndTransform(input as TInput),
      ...cacheSettings,
    })
  }) as QueryFactory<TInput, TResult, TContext>['query']

  return {
    useQuery: useQueryHook,
    useCacheController,
    query,
    getQueryKey: (input) => computeKey(input),
    invalidate: (client, options) => invalidateImpl(client, options),
  }
}
